import * as React from 'react'
import * as Redux from 'react-redux'

import { styled } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import { t, tn } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import { text } from '@owl-nest/utils'
import { CardTypeSelector } from '@ulule/duvet/CardTypeSelector.tsx'
import * as hooks from '@owl-nest/hooks'
import * as env from '@owl-nest/config'

import * as modalAction from '../../../../actions/modal'

import { useProjectFollowing } from '../hooks/useProjectFollowing'

import * as UFE from '../../../../UFE'

type SuggestedProjectProps = {
  project: api.Project
  className?: string
}

type Action = {
  isCompact: boolean
  text: string
  icon: 'inactive-heart' | 'active-heart' | 'created' | 'backed'
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

function SuggestedProjectComponent({
  project,
  className,
}: SuggestedProjectProps): React.ReactElement<SuggestedProjectProps> {
  const dispatch = Redux.useDispatch()
  const isProjectFinished = model.project.isFinished(project)
  const textFinishProject =
    isProjectFinished && project.date_end ? model.project.getFinishedLabel(project.date_end) : t('Finished')
  const [userFollowsProject, setUserFollowsProject] = React.useState(project?.user_role === api.UserRole.Fan)
  const followingStatusRef = React.useRef(true)
  const shortCountdown =
    project.date_end &&
    hooks.useDateCountdown(new Date(project.date_end), { automaticRefresh: true, shortLabels: true })

  const toggleFollowing = useProjectFollowing({
    callback: () => {
      setUserFollowsProject(!userFollowsProject)
    },
    project,
    userFollowsProject,
  })

  const action = getAction(project, toggleFollowing, userFollowsProject)

  const userCurrency = env.getUserConfig().currency

  React.useEffect(() => {
    if (!followingStatusRef.current && userFollowsProject) {
      dispatch(modalAction.toggleModalFollow(true))
    } else followingStatusRef.current = userFollowsProject
  }, [userFollowsProject])

  return (
    <CardTypeSelector
      cardType="classic"
      action={action}
      className={className}
      project={project}
      rates={env.CURRENCIES_RATES}
      targetCurrency={userCurrency}
      projectTranslation={{
        addToMyList: t('Add to my list'),
        listed: t('Listed'),
        backerCount: tn(
          '%s contribution',
          '%s contributions',
          project.orders_count || 0,
          text.numberToLocaleString(project.orders_count || project.supporters_count),
        ),
        comingSoon: model.project.comingSoonLabel(project, Boolean(UFE.CURRENT_USER.is_staff)),
        extraTime: t('Extension'),
        finished: textFinishProject,
        highlights: t('Highlights'),
        progressInfo: t('%(percent)s reached', {
          percent: `${text.numberToLocaleString(model.project.progress(project))}%`,
        }),
        shortCountdown: `${shortCountdown ? shortCountdown.label : ''}`,
        status: model.project.getStatus(project) ?? (shortCountdown ? shortCountdown.label : undefined),
      }}
    />
  )
}

export const SuggestedProject = styled(SuggestedProjectComponent)``

function getAction(project: api.Project, toggleFollowing: () => void, userFollowsProject: boolean): Action {
  const auth = useAuth()
  const isUserLoggedin = auth.type === 'loggedin'
  const dispatch = Redux.useDispatch()

  if (
    project?.user_role === api.UserRole.Owner ||
    project?.user_role === api.UserRole.Moderator ||
    project?.user_role === api.UserRole.Editor
  ) {
    return {
      isCompact: true,
      text: t('Created'),
      icon: 'created',
    }
  }

  if (project?.user_role === api.UserRole.Supporter) {
    return {
      isCompact: true,
      text: t('Backed'),
      icon: 'backed',
    }
  }
  return {
    isCompact: true,
    text: userFollowsProject ? t('Listed') : t('Add to my list'),
    icon: userFollowsProject ? 'active-heart' : 'inactive-heart',
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (isUserLoggedin) {
        toggleFollowing()
      } else {
        dispatch(modalAction.toggleModalFollow(true))
      }
    },
  }
}
